/**
 * Icon + label + chevron dropdown matching PipelineToolbar menus.
 * Filter mode (value) or action mode (no value) for Cap-raise Email/Export/Import.
 * Menu is portaled to document.body so it stacks above drawers (z-99/100).
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowPathIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';
import {
  dropdownTriggerIconClass,
  toolbarDropdownPanelPortaled,
  toolbarMenuItem,
  toolbarMenuItemActive,
  toolbarMenuItemDanger,
  toolbarMenuItemInactive,
  toolbarMenuItemLabel,
  toolbarStandaloneActive,
  toolbarStandaloneBase,
  toolbarStandaloneInactive,
} from './listToolbarTokens';

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

export type ToolbarIconDropdownItem = {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
  tone?: 'default' | 'danger';
};

export type ToolbarIconDropdownProps = {
  'aria-label': string;
  label: string;
  icon?: IconType;
  items: ToolbarIconDropdownItem[];
  value?: string | null;
  active?: boolean;
  onSelect: (id: string) => void;
  disabled?: boolean;
  /** Shows spinner on trigger; does not block opening unless `disabled` is also set. */
  loading?: boolean;
  /**
   * When true, trigger stays visually enabled but menu will not open.
   * Use with `title` / `onBlockedOpen` for empty-selection guidance.
   */
  blockOpen?: boolean;
  /** Native title / tooltip (works even when blockOpen — unlike disabled). */
  title?: string;
  /** Called when user clicks trigger while `blockOpen` is true. */
  onBlockedOpen?: () => void;
  menuClassName?: string;
  menuAlign?: 'left' | 'right';
  className?: string;
  triggerClassName?: string;
};

const MENU_MARGIN = 8;
const MENU_GAP = 4;
const MENU_FALLBACK_WIDTH = 192;

const ToolbarIconDropdown: React.FC<ToolbarIconDropdownProps> = ({
  'aria-label': ariaLabel,
  label,
  icon: Icon,
  items,
  value = null,
  active: activeProp,
  onSelect,
  disabled = false,
  loading = false,
  blockOpen = false,
  title,
  onBlockedOpen,
  menuClassName = '',
  menuAlign = 'left',
  className = '',
  triggerClassName = '',
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const isActive =
    activeProp !== undefined
      ? activeProp
      : value != null && value !== '' && value !== 'all';

  const isDisabled = disabled;

  const placeMenu = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const panelWidth = menuRef.current?.offsetWidth || MENU_FALLBACK_WIDTH;
    let left =
      menuAlign === 'right' ? rect.right - panelWidth : rect.left;
    if (left < MENU_MARGIN) left = MENU_MARGIN;
    if (left + panelWidth > window.innerWidth - MENU_MARGIN) {
      left = window.innerWidth - panelWidth - MENU_MARGIN;
    }
    setMenuPos({ top: rect.bottom + MENU_GAP, left });
  }, [menuAlign]);

  useLayoutEffect(() => {
    if (!open || blockOpen) {
      setMenuPos(null);
      return;
    }
    placeMenu();
  }, [open, blockOpen, placeMenu, items.length]);

  useEffect(() => {
    if (!open || blockOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, blockOpen, placeMenu]);

  useEffect(() => {
    if (blockOpen && open) setOpen(false);
  }, [blockOpen, open]);

  const handleTriggerClick = () => {
    if (isDisabled) return;
    if (blockOpen) {
      onBlockedOpen?.();
      return;
    }
    setOpen((prev) => !prev);
  };

  const menu =
    open && !blockOpen && menuPos
      ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            data-menu-align={menuAlign}
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              zIndex: 10000,
            }}
            className={`${toolbarDropdownPanelPortaled} ${menuClassName}`}
          >
            {items.map((item) => {
              const selected = value != null && value === item.id;
              const itemClass =
                item.tone === 'danger'
                  ? toolbarMenuItemDanger
                  : selected
                    ? toolbarMenuItemActive
                    : toolbarMenuItemInactive;

              return (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={() => {
                    if (item.disabled) return;
                    onSelect(item.id);
                    setOpen(false);
                  }}
                  className={`${toolbarMenuItem} ${itemClass} ${
                    item.disabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <span className={toolbarMenuItemLabel}>{item.label}</span>
                  {item.description ? (
                    <span className="mt-0.5 block text-xs font-normal text-primary-500/70 whitespace-normal">
                      {item.description}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} className={`relative z-20 ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleTriggerClick}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-busy={loading || undefined}
        title={title}
        disabled={isDisabled}
        className={`${toolbarStandaloneBase} justify-between gap-1.5 ${
          isActive ? toolbarStandaloneActive : toolbarStandaloneInactive
        } ${isDisabled ? 'opacity-70 cursor-not-allowed' : ''} ${triggerClassName}`}
      >
        <span className="flex min-w-0 items-center gap-1.5 truncate">
          {loading ? (
            <ArrowPathIcon
              className={`${dropdownTriggerIconClass(isActive)} animate-spin`}
              aria-hidden
            />
          ) : Icon ? (
            <Icon className={dropdownTriggerIconClass(isActive)} aria-hidden />
          ) : null}
          <span className="truncate font-semibold">{label}</span>
        </span>
        <ChevronDownIcon
          className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          } ${isActive ? 'text-white/80' : 'text-primary-400'}`}
          aria-hidden
        />
      </button>
      {menu}
    </div>
  );
};

export default ToolbarIconDropdown;
